import { isEqual } from 'shared/comparers';

import { booleanProblem as ohioBooleanProblem, singleProblem as ohioSingleProblem } from '../components/ohio/utils';
import { booleanProblem as illinoisBooleanProblem } from '../components/illinois/utils';
import { booleanProblem as floridaBooleanProblem } from '../components/florida/utils';

import { OHIO, FLORIDA, ILLINOIS, useConstants } from '../constants';

import type { IManagedScale, IPayScaleQualification, Problems } from '../types';

export function useProblemsHelpers(state: string) {
    const {
        DEGREE,
        ECE_DEGREE,
        MINIMUM_WAGE_BY_STATE,
        OH_SINGLE_QUALIFICATION_PROBLEM_CHOICE_IDS,
        IL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS,
        FL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS,
    } = useConstants(state);

    return {
        baseEducationProblem,
        checkForDuplicates,
        updateProblems,
        findProblems,
    };

    function rateProblem(scale: IManagedScale, state: string): 'toolow' | 'bad' | null {
        if (isNaN(scale.rate)) {
            return 'bad';
        }
        if (scale.rate < MINIMUM_WAGE_BY_STATE[state as keyof typeof MINIMUM_WAGE_BY_STATE]) {
            return 'toolow';
        }

        return null;
    }

    function baseEducationProblem(scale: IManagedScale): boolean {
        const education = scale.qualifications.find((x: IPayScaleQualification) =>
            [DEGREE, ECE_DEGREE].includes(x.name),
        );
        return ![-1, 0, 22, null].includes(education?.option_id ?? null);
    }

    function checkForDuplicates(scales: IManagedScale[]): { [key: string]: boolean } {
        const qualificationsMap: { [key: string]: object } = {};

        scales.forEach((x: IManagedScale) => {
            const qualificationMap = {} as { [key: string]: number | string | null };
            x.qualifications.map((y: IPayScaleQualification) => {
                qualificationMap[`${y.qualification_id}`] = y.option_id ?? y.value;
            });
            qualificationsMap[`${x.id}`] = qualificationMap;
        });

        const duplicateMap: { [key: string]: boolean } = {};

        scales.forEach((x: IManagedScale, index: number) => {
            const qualificationMap = qualificationsMap[`${x.id}`];

            scales.slice(index + 1).forEach((y: IManagedScale) => {
                const otherQualificationMap = qualificationsMap[`${y.id}`];

                if (isEqual(qualificationMap, otherQualificationMap)) {
                    duplicateMap[`${x.id}`] = true;
                    duplicateMap[`${y.id}`] = true;
                }
            });
        });

        return duplicateMap;
    }

    function findProblems(
        scale: IManagedScale,
        state: string,
        initialScale: boolean = false,
        initialScaleId: string = '',
    ): Problems {
        const problems = {
            education: false,
            experience: false,
            single: [],
            multi: [],
            boolean: {},
            rate: null,
            noQualifications: false,
            duplicate: false,
        } as Problems;

        problems.education = initialScale ? baseEducationProblem(scale) : false;
        problems.experience = false;
        problems.noQualifications = !initialScale && scale.id !== initialScaleId && scale.qualifications.length < 1;

        const booleans = scale.qualifications.filter(
            (x: IPayScaleQualification) => !x.option_id && x.value?.toString() === 'true',
        );

        switch (state) {
            case OHIO:
                problems.single = initialScale
                    ? ohioSingleProblem(scale.qualifications, OH_SINGLE_QUALIFICATION_PROBLEM_CHOICE_IDS)
                    : [];
                problems.multi = {};
                problems.boolean = initialScale ? ohioBooleanProblem(booleans) : {};
                problems.rate = rateProblem(scale, state);
                break;
            case FLORIDA:
                problems.single = {};
                problems.multi = {};
                problems.boolean = initialScale
                    ? floridaBooleanProblem(booleans, FL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS)
                    : {};
                problems.rate = rateProblem(scale, state);
                break;
            case ILLINOIS:
                problems.single = {};
                problems.multi = {};
                problems.boolean = initialScale
                    ? illinoisBooleanProblem(booleans, IL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS)
                    : {};
                problems.rate = rateProblem(scale, state);
                break;
            default:
                problems.single = {};
                problems.multi = {};
                problems.boolean = {};
                problems.rate = rateProblem(scale, state);
                break;
        }

        return problems;
    }

    function updateProblems(
        scale: IManagedScale,
        problems: { [key: string]: Problems },
        setProblems: (problems: { [key: string]: Problems }) => void,
        state: string,
    ): void {
        const newProblems = findProblems(scale, state);

        setProblems({ ...problems, [scale.id]: newProblems });
    }
}
